import type {
  GridApi,
  GridCellParams,
  GridCellSelectionModel,
  GridColDef,
  GridRowSelectionModel,
  GridSingleSelectColDef,
} from '@repo/go-table';
import { format, getUnixTime, isDate, isValid, set } from 'date-fns';
import i18nextInstance from 'i18next';
import { action, computed, makeObservable, observable } from 'mobx';
import type { MutableRefObject } from 'react';

import { convertExcelStringToValueArrays } from '../../shared/convertExcelStringToValueArrays';
import type { INotificationsService } from '../../shared/INotificationsService';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';

import { DisplayManager, SERVICE_COLUMNS } from './services/ManualAnomaliesDisplayManager';
import type { ManualAnomaliesService } from './services/ManualAnomaliesService';
import type { ManualAnomaly, TData } from './services/ManualAnomaly.entity';
import { type TManualAnomalyRowData } from './types';

const CLEARABLE_FIELDS = new Set<keyof TManualAnomalyRowData | 'eventDate' | 'eventTime'>([
  'indexFormat',
  'wellboreId',
  'anomalyAt',
  'eventDate',
  'eventTime',
  'riskLevel',
  'anomalyTypeId',
  'anomalySubtypeId',
  'bitDepth',
  'unitId',
  'limitValue',
  'factValue',
  'notification',
  'recommendation',
]);

type TClipboardValue = string | number | boolean | Date | null;
type TAnomalyAtColumnField = 'eventDate' | 'eventTime';

const TIME_CLIPBOARD_VALUE_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

export class ManualAnomaliesTableStore {
  private tableApi: MutableRefObject<GridApi>;
  @observable private isMainListenersInitialized = false;

  @observable displayManager: DisplayManager;
  @observable selectedRows: GridRowSelectionModel = [];

  @observable isTableSettingsOpen = false;

  @observable contextColumnsHidden = false;

  @observable isRowSelectionModeOn = false;

  constructor(
    tableApi: MutableRefObject<GridApi>,
    private readonly notifications: INotificationsService,
    private readonly manualAnomaliesService: ManualAnomaliesService,
    private readonly timeZonesStore: TimeZonesStore,
  ) {
    this.tableApi = tableApi;
    this.displayManager = new DisplayManager(this.manualAnomaliesService.settingsStore);
    this.contextColumnsHidden = this.displayManager.contextColumnsHidden;
    makeObservable(this);
  }

  @computed
  get isInitialized(): boolean {
    return this.isMainListenersInitialized && this.displayManager != null;
  }

  @computed
  get isLoading(): boolean {
    return this.manualAnomaliesService.loading;
  }

  get isEditing(): boolean {
    return this.manualAnomaliesService.uiState.editing;
  }

  @computed
  get isRowSelectingEnabled(): boolean {
    return this.isEditing || this.isRowSelectionModeOn;
  }

  @action.bound
  private addAnomalies(rows: ManualAnomaly[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);

    const lastAnomaly = rows[rows.length - 1];

    if (lastAnomaly) {
      setTimeout(() => {
        this.tableApi?.current.setCellFocus(lastAnomaly.entityId, 'anomalyTypeId');
        this.tableApi?.current.startCellEditMode({ id: lastAnomaly.entityId, field: 'anomalyTypeId' });
      }, 100);
    }
  }

  @action.bound
  private updateAnomalies(rows: ManualAnomaly[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);

    this.tableApi.current.updateRows(rows);
  }

  @action.bound
  private deleteAnomalies(ids: string[]): void {
    if (!this.tableApi || !ids.length) return;
    this.tableApi.current.updateRows(ids.map((id) => ({ entityId: id, _action: 'delete' })));
  }

  getFieldValue = <T extends keyof TManualAnomalyRowData>(rowId: string, field: T): TManualAnomalyRowData[T] | null => {
    const record = this.manualAnomaliesService.manualAnomaliesMap.get(rowId);

    const fieldValue = record?.data[field] ?? null;

    if (fieldValue == null) {
      return null;
    }

    return fieldValue as TManualAnomalyRowData[T];
  };

  @action.bound
  onManualAnomalyItemValueChange<T extends keyof TManualAnomalyRowData>(
    rowId: string,
    field: T,
    value: TManualAnomalyRowData[T],
  ): void {
    this.manualAnomaliesService.onManualAnomalyItemValueChange(rowId, field, value);
  }

  @action.bound
  setSelectedRows(model: GridRowSelectionModel): void {
    this.selectedRows = model;
    this.tableApi.current.setRowSelectionModel(model);
  }

  isCellEditable(params: GridCellParams<ManualAnomaly>): boolean {
    return this.isFieldEditable(params.field, params.colDef, params.row.data);
  }

  private isFieldEditable(field: string, colDef: GridColDef, rowData: Partial<TData>): boolean {
    if (!colDef.editable) {
      return false;
    }

    const isLogTimeFormat = rowData.indexFormat === 'LOG_TIME';

    if (field === 'eventDate' || field === 'eventTime') {
      return isLogTimeFormat;
    }

    if (field === 'wellboreId' || field === 'bitDepth') {
      return !isLogTimeFormat;
    }

    return true;
  }

  private isCellClearable(rowId: string, field: string): boolean {
    const row = this.manualAnomaliesService.manualAnomaliesMap.get(rowId);

    if (!row || !CLEARABLE_FIELDS.has(field as keyof TManualAnomalyRowData | 'eventDate' | 'eventTime')) {
      return false;
    }

    const column = this.tableApi.current.getColumn(field);

    if (!column) {
      return false;
    }

    return this.isCellEditable({ id: rowId, field, row, colDef: column } as GridCellParams<ManualAnomaly>);
  }

  private getClearDataField(field: string): keyof TManualAnomalyRowData | null {
    if (field === 'eventDate' || field === 'eventTime') {
      return 'anomalyAt';
    }

    if (CLEARABLE_FIELDS.has(field as keyof TManualAnomalyRowData)) {
      return field as keyof TManualAnomalyRowData;
    }

    return null;
  }

  private getClearValue(field: keyof TManualAnomalyRowData): string | null {
    if (field === 'notification' || field === 'recommendation') {
      return '';
    }

    return null;
  }

  @action.bound
  handleDelete(params: GridCellParams<ManualAnomaly>): void {
    if (!this.isEditing) {
      return;
    }

    const cellSelection = this.tableApi.current.getCellSelectionModel();
    const targetCells =
      cellSelection && Object.keys(cellSelection).length
        ? cellSelection
        : {
            [params.id]: {
              [params.field]: true,
            },
          };
    const patches: [string, Partial<TData>][] = [];

    for (const rowId in targetCells) {
      const patch: Partial<TData> = {};

      for (const field in targetCells[rowId]) {
        if (!targetCells[rowId][field]) {
          continue;
        }

        const dataField = this.getClearDataField(field);

        if (dataField && this.isCellClearable(rowId, field)) {
          (patch as Record<string, unknown>)[dataField] = this.getClearValue(dataField);
        }
      }

      if (Object.keys(patch).length) {
        patches.push([rowId, patch]);
      }
    }

    if (patches.length) {
      this.manualAnomaliesService.applyManualAnomalyItemPatches(patches);
    }
  }

  @action.bound
  setIsTableSettingsOpen(is: boolean): void {
    this.isTableSettingsOpen = is;
  }

  @action.bound
  toggleContextColumnsHidden(): void {
    const contextColumnsHidden = !this.contextColumnsHidden;
    this.contextColumnsHidden = contextColumnsHidden;
    this.displayManager.onContextColumnsHiddenChange(contextColumnsHidden);
  }

  @action.bound
  setIsRowSelectionModeOn(is: boolean): void {
    this.isRowSelectionModeOn = is;
  }

  private getAnomalySubtypeOptions() {
    return this.manualAnomaliesService.anomalySubtypes.map((anomalySubtype) => ({
      label: anomalySubtype.data['name']?.toString() ?? '',
      value: anomalySubtype.id as number,
    }));
  }

  private isAnomalyAtColumnField(field: string): field is TAnomalyAtColumnField {
    return field === 'eventDate' || field === 'eventTime';
  }

  private getZonedAnomalyAt(anomalyAt: TData['anomalyAt']): Date {
    const anomalyAtMilliseconds = typeof anomalyAt === 'number' ? anomalyAt * 1000 : Date.now();

    return this.timeZonesStore.getDate(anomalyAtMilliseconds);
  }

  private getAnomalyAtFromZonedDate(date: Date): number {
    return getUnixTime(this.timeZonesStore.getInverDate(date.getTime()));
  }

  private getTimeFromClipboardValue(value: Exclude<TClipboardValue, null>) {
    if (isDate(value) && isValid(value)) {
      return {
        hours: value.getHours(),
        minutes: value.getMinutes(),
        seconds: value.getSeconds(),
      };
    }

    if (typeof value !== 'string') {
      return null;
    }

    const match = value.match(TIME_CLIPBOARD_VALUE_PATTERN);

    if (!match) {
      return null;
    }

    return {
      hours: Number(match[1]),
      minutes: Number(match[2]),
      seconds: Number(match[3] ?? 0),
    };
  }

  private getPastedAnomalyAt(
    field: TAnomalyAtColumnField,
    value: TClipboardValue,
    anomalyAt: TData['anomalyAt'],
  ): number | null | undefined {
    if (value === null) {
      return null;
    }
    const zonedAnomalyAt = this.getZonedAnomalyAt(anomalyAt);

    if (field === 'eventDate') {
      if (!isDate(value) || !isValid(value)) {
        return undefined;
      }

      return this.getAnomalyAtFromZonedDate(
        set(zonedAnomalyAt, {
          year: value.getFullYear(),
          month: value.getMonth(),
          date: value.getDate(),
        }),
      );
    }

    const time = this.getTimeFromClipboardValue(value);

    if (!time) {
      return undefined;
    }

    return this.getAnomalyAtFromZonedDate(
      set(zonedAnomalyAt, {
        hours: time.hours,
        minutes: time.minutes,
        seconds: time.seconds,
        milliseconds: 0,
      }),
    );
  }

  private parsePasteDataToManualAnomalyData(
    columns: GridColDef[],
    rows: TClipboardValue[][],
    existingRowIds: string[] = [],
  ): TData[] {
    const data = columns.reduce((acc: TData[], col, columnIndex) => {
      rows.forEach((row, rowIndex) => {
        if (columnIndex >= row.length) {
          return;
        }

        const data = { ...(acc[rowIndex] ?? {}) };
        const existingRowId = existingRowIds[rowIndex];
        const existingAnomaly = existingRowId
          ? this.manualAnomaliesService.manualAnomaliesMap.get(existingRowId)
          : null;
        const rowData = { ...(existingAnomaly?.data ?? {}), ...data };

        if (existingAnomaly && !this.isFieldEditable(col.field, col, rowData)) {
          return;
        }

        const columnValue = row[columnIndex];

        if (this.isAnomalyAtColumnField(col.field)) {
          const anomalyAt = this.getPastedAnomalyAt(col.field, columnValue, rowData.anomalyAt);

          if (anomalyAt !== undefined) {
            data.anomalyAt = anomalyAt;
          }

          acc[rowIndex] = data;
          return;
        }

        if (columnValue === null) {
          data[col.field] = null;
        } else {
          switch (col.type) {
            case 'singleSelect': {
              const optionsRaw = (col as GridSingleSelectColDef).valueOptions;

              let options = (() => {
                if (Array.isArray(optionsRaw)) {
                  return optionsRaw;
                }

                return optionsRaw?.({ field: col.field, row: { data: rowData } }) ?? [];
              })();

              if (col.field === 'anomalySubtypeId' && !options.length) {
                options = this.getAnomalySubtypeOptions();
              }

              const value = options.find((o) => {
                if (typeof o === 'string' || typeof o === 'number') {
                  return o.toString().toLowerCase() === columnValue.toString().toLowerCase();
                }

                return o.label.toString().toLowerCase() === columnValue.toString().toLowerCase();
              });

              if (value != null) {
                if (typeof value === 'string' || typeof value === 'number') {
                  data[col.field] = value;
                } else {
                  data[col.field] = value.value;
                }
              }

              break;
            }

            case 'date':
            case 'dateTime': {
              if (isDate(columnValue)) {
                data[col.field] = getUnixTime(columnValue as Date);
              } else {
                console.warn(`given value for column ${col.field} is not date`, columnValue);
              }

              break;
            }

            case 'string': {
              if (typeof columnValue === 'string') {
                data[col.field] = columnValue;
              } else {
                data[col.field] = columnValue.toString();
              }

              break;
            }

            case 'boolean': {
              if (typeof columnValue === 'boolean') {
                data[col.field] = columnValue;
              } else {
                data[col.field] = !!columnValue;
              }

              break;
            }

            case 'number': {
              if (typeof columnValue === 'number') {
                data[col.field] = columnValue;
                break;
              }

              if (columnValue == null) {
                data[col.field] = null;
                break;
              }

              const parsedValue = Number(columnValue);

              if (!Number.isNaN(parsedValue)) {
                data[col.field] = parsedValue;
              } else {
                data[col.field] = null;
              }

              break;
            }

            default: {
              data[col.field] = null;
            }
          }
        }

        acc[rowIndex] = data;
      });

      return acc;
    }, []);

    return data;
  }

  private getClipboardCellValue(params: GridCellParams | null | undefined): string {
    if (!params) {
      return '-';
    }

    if (this.isAnomalyAtColumnField(params.field) && typeof params.value === 'number') {
      const date = this.getZonedAnomalyAt(params.value);

      return format(date, params.field === 'eventDate' ? 'dd.MM.yyyy' : 'HH:mm:ss');
    }

    return params.formattedValue?.toString() ?? params.value?.toString() ?? '-';
  }

  private copySelectedRowsToClipboard = async () => {
    const api = this.tableApi?.current;
    if (!api) return;

    const rowsVisibility = api.state.filter.filteredRowsLookup;
    const columnsVisibility = api.state.columns.columnVisibilityModel;

    const sortedRows = api.getSortedRows() as Array<{ entityId?: string; id?: string }>;
    const selectedRows = api.getSelectedRows();
    const filteredRows = sortedRows.filter((row) => {
      const id = row.entityId ?? row.id;
      return id != null && selectedRows.get(id) && rowsVisibility[id];
    });
    const columns = api.getAllColumns();

    const copyData = filteredRows
      .map((row) => {
        const id = row.entityId ?? row.id;
        if (!id) {
          return '';
        }
        const values: string[] = [];
        for (const { field } of columns) {
          if (columnsVisibility[field] === false || SERVICE_COLUMNS.includes(field) || field === '__check__') continue;
          const cellParams = api.getCellParams(id, field);
          values.push(this.getClipboardCellValue(cellParams));
        }
        return values.join('\t');
      })
      .filter(Boolean)
      .join('\n');

    try {
      await navigator.clipboard.writeText(copyData);
      this.setSelectedRows([]);
      this.notifications.showSuccessMessageT('alertsManagerWidget:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
    }
  };

  private copySelectedCellData = async (selectedCellData: GridCellSelectionModel) => {
    const api = this.tableApi?.current;
    if (!api) return;

    const dataRows: string[] = [];
    for (const rowId in selectedCellData) {
      const values: string[] = [];
      for (const fieldId in selectedCellData[rowId]) {
        const cellParams = api.getCellParams(rowId, fieldId);
        values.push(this.getClipboardCellValue(cellParams));
      }
      dataRows.push(values.join('\t'));
    }

    const copyData = dataRows.join('\n');
    try {
      await navigator.clipboard.writeText(copyData);
      api.setRowSelectionModel([]);
      this.notifications.showSuccessMessageT('alertsManagerWidget:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
    }
  };

  @action.bound
  handleCopy() {
    const api = this.tableApi;

    if (!api) {
      return;
    }

    const selectedRows = api.current.getSelectedRows();

    if (selectedRows.size < 1) {
      const cellSelection = api.current.getCellSelectionModel();
      const focusedCellData = api.current.state.focus.cell;

      const tableCopyTargetData = ((): GridCellSelectionModel | null => {
        if (cellSelection && !!Object.keys(cellSelection).length) {
          return cellSelection;
        }

        if (focusedCellData) {
          return {
            [focusedCellData.id]: {
              [focusedCellData.field]: true,
            },
          };
        }

        return null;
      })();

      if (!tableCopyTargetData) {
        return;
      }

      this.copySelectedCellData(tableCopyTargetData);

      return;
    }

    this.copySelectedRowsToClipboard();
  }

  @action.bound
  async handlePaste(): Promise<void> {
    const api = this.tableApi?.current;

    if (!api || !this.isEditing) {
      return;
    }

    const focusedCellData = api.state.focus.cell;

    let clipboardData = '';

    try {
      clipboardData = await navigator.clipboard.readText();
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsManagerWidget:errors.blockedCopyPaste');
      return;
    }

    if (!clipboardData || typeof clipboardData !== 'string') {
      return;
    }

    const columns = api
      .getVisibleColumns()
      .filter((c) => !SERVICE_COLUMNS.includes(c.field) && c.field !== '__check__');
    const initialDataRows = convertExcelStringToValueArrays(clipboardData, {
      boolean: {
        true: [i18nextInstance.t('common:yes')],
        false: [i18nextInstance.t('common:no')],
      },
    }).filter((row) => !row.every((val) => val === null));

    if (!focusedCellData) {
      const manualAnomalyData = this.parsePasteDataToManualAnomalyData(columns, initialDataRows);
      this.manualAnomaliesService.addNewManualAnomalyItemPatches(manualAnomalyData);
      return;
    }

    const pasteTargetColumnIndex = columns.findIndex((c) => c.field === focusedCellData.field);
    const existingRows = (() => {
      const allRowsIds = api.getSortedRowIds();
      const index = allRowsIds.indexOf(focusedCellData.id);

      return allRowsIds.slice(index, index + initialDataRows.length);
    })();

    const manualAnomalyData = this.parsePasteDataToManualAnomalyData(
      columns.slice(pasteTargetColumnIndex),
      initialDataRows,
      existingRows.map(String),
    );

    const updateAnomalyData = manualAnomalyData.slice(0, existingRows.length);
    const newAnomalyData = manualAnomalyData.slice(existingRows.length);

    this.manualAnomaliesService.applyManualAnomalyItemPatches(
      existingRows.map((rowId, index) => [rowId as string, updateAnomalyData[index]]),
    );

    this.manualAnomaliesService.addNewManualAnomalyItemPatches(newAnomalyData);
  }

  @action.bound
  init() {
    const newDisposer = this.manualAnomaliesService.eventEmitter.addEventListener(
      'newManualAnomalies',
      this.addAnomalies,
    );
    const updateDisposer = this.manualAnomaliesService.eventEmitter.addEventListener(
      'updateManualAnomalies',
      this.updateAnomalies,
    );
    const deleteDisposer = this.manualAnomaliesService.eventEmitter.addEventListener(
      'deleteManualAnomalies',
      this.deleteAnomalies,
    );

    this.isMainListenersInitialized = true;

    return () => {
      newDisposer();
      updateDisposer();
      deleteDisposer();
    };
  }
}
